import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { QuickQuoteConfig } from 'contexts/serviceContext';
import { Maybe } from 'types/UtilityTypes';

dayjs.extend(utc);

export const TAI_LTL_DEFAULT_PICKUP_CLOSE_OFFSET_HOURS = 4;

export interface TaiLtlPickupWindow {
  pickupDate: Date;
  pickupCloseDate: Date;
}

// The service's usual pickup hours, from Quick Quote config.
export type TaiLtlPickupHours = Pick<
  QuickQuoteConfig,
  'defaultLtlPickupReadyTime' | 'defaultLtlPickupCloseTime'
>;

type ClockTime = { hour: number; minute: number };

const CLOCK_TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

function isValidDate(value: Maybe<Date>): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

function parseClockTime(value: Maybe<string> | undefined): Maybe<ClockTime> {
  const match = CLOCK_TIME_PATTERN.exec(value ?? '');
  if (!match) {
    return null;
  }

  return { hour: Number(match[1]), minute: Number(match[2]) };
}

function minutesOf(time: ClockTime): number {
  return time.hour * 60 + time.minute;
}

type CalendarDay = { year: number; month: number; day: number };

// Tai dates are wall clock stored as UTC, so a bare date from the email is 00:00 UTC. Any other
// time, including one that happens to be the rep's local midnight, is a real pickup time.
function isDateOnly(date: Date): boolean {
  const utc = dayjs.utc(date);

  return utc.isSame(utc.startOf('day'));
}

function atWallClock({ year, month, day }: CalendarDay, time: ClockTime): Date {
  return new Date(Date.UTC(year, month, day, time.hour, time.minute));
}

// Today's window on the rep's calendar, or tomorrow's once today's close has passed.
function windowForToday(
  ready: ClockTime,
  close: ClockTime,
  now: Date
): TaiLtlPickupWindow {
  const local = dayjs(now);
  const nowMinutes = local.hour() * 60 + local.minute();
  const date = nowMinutes >= minutesOf(close) ? local.add(1, 'day') : local;
  const day = { year: date.year(), month: date.month(), day: date.date() };

  return {
    pickupDate: atWallClock(day, ready),
    pickupCloseDate: atWallClock(day, close),
  };
}

// The service's hours on the suggested date. A suggested time of day is kept, and Close only
// moves to the service's close when that is still after Ready.
function windowOnDate(
  suggestedReady: Date,
  ready: ClockTime,
  close: ClockTime
): Maybe<TaiLtlPickupWindow> {
  const utcDate = dayjs.utc(suggestedReady);
  const day = {
    year: utcDate.year(),
    month: utcDate.month(),
    day: utcDate.date(),
  };

  const pickupDate = isDateOnly(suggestedReady)
    ? atWallClock(day, ready)
    : suggestedReady;
  const pickupCloseDate = atWallClock(day, close);

  if (pickupCloseDate.getTime() < pickupDate.getTime()) {
    return null;
  }

  return { pickupDate, pickupCloseDate };
}

export function hasTaiLtlPickupHours(
  pickupHours: Maybe<TaiLtlPickupHours> | undefined
): boolean {
  const readyTime = parseClockTime(pickupHours?.defaultLtlPickupReadyTime);
  const closeTime = parseClockTime(pickupHours?.defaultLtlPickupCloseTime);

  return Boolean(
    readyTime && closeTime && minutesOf(closeTime) > minutesOf(readyTime)
  );
}

// Tai requires a pickup Close to dispatch. When the service has pickup hours, a missing or
// date-only Ready uses them; a suggested Close is kept when it is not before Ready. Without
// pickup hours, Ready defaults to now and Close to Ready+4h.
export function resolveTaiLtlPickupWindow(
  suggestedReady: Maybe<Date>,
  suggestedClose: Maybe<Date>,
  now: Date = new Date(),
  pickupHours?: Maybe<TaiLtlPickupHours>
): TaiLtlPickupWindow {
  const readyTime = parseClockTime(pickupHours?.defaultLtlPickupReadyTime);
  const closeTime = parseClockTime(pickupHours?.defaultLtlPickupCloseTime);
  const hasPickupHours =
    readyTime && closeTime && minutesOf(closeTime) > minutesOf(readyTime);

  let window: Maybe<TaiLtlPickupWindow> = null;
  if (hasPickupHours) {
    window = isValidDate(suggestedReady)
      ? windowOnDate(suggestedReady, readyTime, closeTime)
      : windowForToday(readyTime, closeTime, now);
  }

  const pickupDate =
    window?.pickupDate ?? (isValidDate(suggestedReady) ? suggestedReady : now);

  let pickupCloseDate = dayjs(pickupDate)
    .add(TAI_LTL_DEFAULT_PICKUP_CLOSE_OFFSET_HOURS, 'hour')
    .toDate();
  if (
    isValidDate(suggestedClose) &&
    suggestedClose.getTime() >= pickupDate.getTime()
  ) {
    pickupCloseDate = suggestedClose;
  } else if (window) {
    pickupCloseDate = window.pickupCloseDate;
  }

  const result: TaiLtlPickupWindow = { pickupDate, pickupCloseDate };

  return result;
}

export function isValidDateTimeWindow(
  start: Maybe<Date>,
  end: Maybe<Date>
): boolean {
  if (!isValidDate(start) || !isValidDate(end)) {
    return false;
  }

  return end.getTime() >= start.getTime();
}
